import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, DollarSign, TrendingUp, Users, Building2, AlertTriangle, CheckCircle2, Clock, XCircle, Zap, Mail } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active: { label: "Active", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100 dark:bg-emerald-950/40" },
  trialing: { label: "Trialing", icon: Clock, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-950/40" },
  past_due: { label: "Past Due", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-950/40" },
  grace_period: { label: "Grace Period", icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-100 dark:bg-orange-950/40" },
  unpaid: { label: "Unpaid", icon: XCircle, color: "text-red-600", bg: "bg-red-100 dark:bg-red-950/40" },
  canceled: { label: "Canceled", icon: XCircle, color: "text-slate-500", bg: "bg-slate-100 dark:bg-slate-800" },
};

const ADDON_LABEL = {
  payroll_live: "Payroll LIVE",
  ap_live: "Accounts Payable LIVE",
  ar_live: "Accounts Receivable LIVE",
  pos_live: "POS LIVE",
};

export default function TenantAnalytics() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [waitlistStats, setWaitlistStats] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, growthRes, waitlistRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/growth-chart?months=12`, { headers }),
        axios.get(`${API_URL}/api/waitlist/stats`, { headers }),
      ]);
      setStats(statsRes.data);
      setGrowth(growthRes.data);
      setWaitlistStats(waitlistRes.data);
    } catch (e) {
      console.error("Failed to fetch admin analytics", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="tenant-analytics-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const peakNewSignups = growth?.series ? Math.max(1, ...growth.series.map((s) => s.new_tenants)) : 1;
  const peakCumulative = growth?.series?.length ? growth.series[growth.series.length - 1].cumulative_tenants : 0;

  return (
    <div className="space-y-6" data-testid="tenant-analytics">
      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <Card data-testid="kpi-mrr">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide">
              <DollarSign className="h-4 w-4" />
              MRR
            </CardDescription>
            <CardTitle className="text-3xl">${stats?.mrr?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              ARR ${stats?.arr?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-total-tenants">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide">
              <Building2 className="h-4 w-4" />
              Total Tenants
            </CardDescription>
            <CardTitle className="text-3xl">{stats?.total_tenants}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">{stats?.active_tenants} active</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-active-subs">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide">
              <CheckCircle2 className="h-4 w-4" />
              Paying
            </CardDescription>
            <CardTitle className="text-3xl">{stats?.by_status?.active || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">+ {stats?.by_status?.trialing || 0} trialing</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-at-risk">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide">
              <AlertTriangle className="h-4 w-4" />
              At Risk
            </CardDescription>
            <CardTitle className="text-3xl">
              {(stats?.by_status?.past_due || 0) + (stats?.by_status?.grace_period || 0)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">past_due + grace_period</p>
          </CardContent>
        </Card>
      </div>

      {/* MRR Breakdown by Plan */}
      <Card data-testid="mrr-breakdown-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            MRR Contribution by Plan
          </CardTitle>
          <CardDescription>Billable subscriptions only (active + trialing + grace_period + past_due)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(stats?.mrr_breakdown || {}).map(([plan, amount]) => {
              const pct = stats?.mrr ? (amount / stats.mrr) * 100 : 0;
              const count = stats?.by_plan?.[plan] || 0;
              return (
                <div key={plan} data-testid={`mrr-row-${plan}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium capitalize">{plan}</span>
                      <Badge variant="outline" className="text-xs">{count} tenants</Badge>
                    </div>
                    <span className="text-sm font-mono">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Status Breakdown */}
      <Card data-testid="status-breakdown-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Subscription Statuses
          </CardTitle>
          <CardDescription>Tenant lifecycle distribution</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(STATUS_META).map(([key, meta]) => {
              const count = stats?.by_status?.[key] || 0;
              const Icon = meta.icon;
              return (
                <div
                  key={key}
                  className={`p-3 rounded-lg ${meta.bg} border`}
                  data-testid={`status-pill-${key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                    <span className={`text-2xl font-bold ${meta.color}`}>{count}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{meta.label}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tenant Growth Chart (CSS bars) */}
      <Card data-testid="growth-chart-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Tenant Growth (last 12 months)
          </CardTitle>
          <CardDescription>
            New signups per month · cumulative reached {peakCumulative.toLocaleString()} tenants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-48" data-testid="growth-chart-bars">
            {growth?.series?.map((b) => {
              const h = (b.new_tenants / peakNewSignups) * 100;
              return (
                <div
                  key={b.label}
                  className="flex-1 flex flex-col items-center justify-end group relative"
                  data-testid={`growth-bar-${b.label.replace(/\s+/g, "-")}`}
                  title={`${b.label}: +${b.new_tenants} new · ${b.cumulative_tenants} total`}
                >
                  <div className="text-[10px] font-mono text-muted-foreground mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    +{b.new_tenants}
                  </div>
                  <div
                    className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t hover:from-blue-600 hover:to-cyan-500 transition-colors min-h-[2px]"
                    style={{ height: `${Math.max(h, b.new_tenants > 0 ? 5 : 0)}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-2 px-1">
            {growth?.series?.map((b) => (
              <div key={b.label} className="flex-1 text-center">
                <span className="truncate inline-block max-w-full">{b.label.split(" ")[0]}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Waitlist + Add-on Attach */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="addon-attach-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              LIVE Add-on Attach
            </CardTitle>
            <CardDescription>Tenants with each LIVE add-on enabled</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats?.addon_attach || {}).map(([key, count]) => (
                <div key={key} className="flex items-center justify-between text-sm" data-testid={`addon-attach-${key}`}>
                  <span>{ADDON_LABEL[key] || key}</span>
                  <Badge variant={count > 0 ? "default" : "outline"}>{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="waitlist-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Feature Waitlist
            </CardTitle>
            <CardDescription>Emails captured from the Under-Construction badges</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(waitlistStats?.by_feature || {}).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between text-sm" data-testid={`waitlist-row-${key}`}>
                  <span className="capitalize">{ADDON_LABEL[key] || key.replace(/_/g, " ")}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{val.pending} pending</Badge>
                    {val.notified > 0 && (
                      <Badge variant="secondary">{val.notified} notified</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
